/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { theme } from '../../styles/theme'

const headerStyles = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${theme.dimensions.headerHeight};
  background-color: ${theme.colors.headerBg};
  box-shadow: ${theme.shadows.header};
  z-index: ${theme.zIndex.header};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  
  @media (min-width: ${theme.breakpoints.mobile}) {
    padding: 0 40px;
  }
`

const logoContainerStyles = css`
  height: 40px;
  
  img {
    height: 100%;
  }
`

const navStyles = css`
  display: flex;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`

const mobileNavStyles = css`
  display: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: block;
  }
`

const hamburgerStyles = css`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 30px;
  height: 30px;
  
  span {
    display: block;
    height: 3px;
    width: 100%;
    background-color: #565656;
    transition: all 0.3s ease;
    transform-origin: center;
  }
  
  &.open span:nth-of-type(1) {
    transform: rotate(45deg) translate(6px, 6px);
  }
  
  &.open span:nth-of-type(2) {
    opacity: 0;
  }
  
  &.open span:nth-of-type(3) {
    transform: rotate(-45deg) translate(6px, -6px);
  }
`

const navListStyles = css`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  align-items: baseline;
`

const navItemStyles = css`
  margin-left: 30px;
  position: relative;
  
  &:first-of-type {
    margin-left: 0;
  }
`

const navLinkStyles = css`
  color: #565656;
  font-family: "Raleway", Helvetica, Arial, sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  text-decoration: none;
  padding: 8px 0;
  transition: ${theme.transitions.default};
  cursor: pointer;
  position: relative;
  
  &:hover {
    color: ${theme.colors.primary};
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${theme.colors.primary};
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`

const menuButtonStyles = css`
  /* Reset all button styles to match links exactly */
  all: unset;
  
  /* Apply same styles as navLinkStyles */
  color: #565656;
  font-family: "Raleway", Helvetica, Arial, sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  text-decoration: none;
  padding: 8px 0;
  transition: ${theme.transitions.default};
  cursor: pointer;
  position: relative;
  display: inline;
  
  &:hover {
    color: ${theme.colors.primary};
  }
  
  &:focus {
    outline: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${theme.colors.primary};
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`

const menuItemsStyles = css`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-radius: ${theme.borderRadius.default};
  min-width: 200px;
  padding: 8px 0;
  z-index: 1000;
  margin-top: 8px;
  border: none !important;
  outline: none !important;
  white-space: nowrap;
  
  /* Ensure it doesn't cause layout shifts */
  will-change: transform, opacity;
  
  /* Remove any focus or active states that might show borders */
  &:focus {
    outline: none !important;
    border: none !important;
  }
  
  &:focus-visible {
    outline: none !important;
    border: none !important;
  }
`

const menuItemStyles = css`
  padding: 0;
  cursor: pointer;
  transition: ${theme.transitions.default};
  border: none !important;
  outline: none !important;
  
  &:focus {
    outline: none !important;
    border: none !important;
  }
  
  a {
    color: #565656;
    font-family: "Raleway", Helvetica, Arial, sans-serif;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    text-decoration: none;
    display: block;
    padding: 12px 20px;
    transition: ${theme.transitions.default};
    border: none !important;
    outline: none !important;
    
    &:hover {
      color: ${theme.colors.primary};
      background-color: rgba(41, 117, 186, 0.05);
    }
    
    &:focus {
      outline: none !important;
      border: none !important;
    }
  }
`

const mobileMenuStyles = css`
  position: fixed;
  top: ${theme.dimensions.headerHeight};
  left: 0;
  right: 0;
  background-color: white;
  box-shadow: ${theme.shadows.card};
  padding: 20px;
  transform: translateY(-100%);
  opacity: 0;
  transition: all 0.3s ease;
  z-index: 999;
  
  &.open {
    transform: translateY(0);
    opacity: 1;
  }
`

const mobileNavListStyles = css`
  list-style: none;
  margin: 0;
  padding: 0;
`

const mobileNavItemStyles = css`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`

const mobileNavLinkStyles = css`
  color: #565656;
  font-family: "Raleway", Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  text-decoration: none;
  display: block;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
  
  &:hover {
    color: ${theme.colors.primary};
  }
`

const mobileSubItemStyles = css`
  margin-left: 20px;
  margin-top: 10px;
  
  a {
    color: #565656;
    font-family: "Raleway", Helvetica, Arial, sans-serif;
    font-size: 13px;
    font-weight: 500;
    text-transform: uppercase;
    text-decoration: none;
    border-bottom: none;
    padding: 5px 0;
    display: block;
    transition: ${theme.transitions.default};
    
    &:hover {
      color: ${theme.colors.primary};
    }
  }
`

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  return (
    <>
      <header css={headerStyles}>
        <div css={logoContainerStyles}>
          <Link to="/">
            <img 
              src="/images/zehnlabs-logo.webp"
              alt="Zehnlabs" 
            />
          </Link>
        </div>
        
        {/* Desktop Navigation */}
        <nav css={navStyles}>
          <ul css={navListStyles}>
            <li css={navItemStyles}>
              <Link to="/" css={navLinkStyles}>Home</Link>
            </li>
            <li css={navItemStyles}>
              <Menu as="div" css={css`position: relative;`}>
                <Menu.Button css={menuButtonStyles}>
                  Resources
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <Menu.Items css={menuItemsStyles} modal={false}>
                    <Menu.Item>
                      {({ close }) => (
                        <div css={menuItemStyles}>
                          <Link to="/videos" onClick={() => close()}>Videos</Link>
                        </div>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ close }) => (
                        <div css={menuItemStyles}>
                          <Link to="/articles" onClick={() => close()}>Articles</Link>
                        </div>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ close }) => (
                        <div css={menuItemStyles}>
                          <Link to="/retirement-planner" onClick={() => close()}>Retirement Planner</Link>
                        </div>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ close }) => (
                        <div css={menuItemStyles}>
                          <Link to="/performance-metrics/glossary" onClick={() => close()}>Performance Metrics Glossary</Link>
                        </div>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </li>
            <li css={navItemStyles}>
              <Link to="/faq" css={navLinkStyles}>FAQ</Link>
            </li>
            <li css={navItemStyles}>
              <Link to="/support" css={navLinkStyles}>Support</Link>
            </li>
          </ul>
        </nav>
        
        {/* Mobile Hamburger */}
        <div css={mobileNavStyles}>
          <button 
            css={hamburgerStyles}
            className={mobileMenuOpen ? 'open' : ''}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>
      
      {/* Mobile Menu */}
      <div css={mobileMenuStyles} className={mobileMenuOpen ? 'open' : ''}>
        <ul css={mobileNavListStyles}>
          <li css={mobileNavItemStyles}>
            <Link to="/" css={mobileNavLinkStyles} onClick={() => setMobileMenuOpen(false)}>
              Home
            </Link>
          </li>
          <li css={mobileNavItemStyles}>
            <span css={mobileNavLinkStyles}>Resources</span>
            <div css={mobileSubItemStyles}>
              <Link to="/videos" onClick={() => setMobileMenuOpen(false)}>Videos</Link>
            </div>
            <div css={mobileSubItemStyles}>
              <Link to="/articles" onClick={() => setMobileMenuOpen(false)}>Articles</Link>
            </div>
            <div css={mobileSubItemStyles}>
              <Link to="/retirement-planner" onClick={() => setMobileMenuOpen(false)}>Retirement Planner</Link>
            </div>
            <div css={mobileSubItemStyles}>
              <Link to="/performance-metrics/glossary" onClick={() => setMobileMenuOpen(false)}>Performance Metrics Glossary</Link>
            </div>
          </li>
          <li css={mobileNavItemStyles}>
            <Link to="/faq" css={mobileNavLinkStyles} onClick={() => setMobileMenuOpen(false)}>
              FAQ
            </Link>
          </li>
          <li css={mobileNavItemStyles}>
            <Link to="/support" css={mobileNavLinkStyles} onClick={() => setMobileMenuOpen(false)}>
              Support
            </Link>
          </li>
        </ul>
      </div>
    </>
  )
}